import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ACTIVITY_TYPE_MAP,
  MESSAGE_CHANNEL_MAP,
  whatsappNumber,
  type MessageChannel,
} from '@goyone/shared';
import { api, qs, upload } from '../../lib/api';
import { dateTime, plural, WORDS } from '../../lib/format';
import { Icon } from '../../components/Icon';
import { Modal, SearchBox } from '../../components/ui';
import { MailCompose } from '../../components/MailCompose';
import { QuickMessageModal, type QuickChannel } from '../../components/QuickMessage';
import { QuickCallModal } from '../../components/QuickCall';
import { useToast } from '../../components/Toast';
import { usePagedNewestFirst } from '../../components/Pager';
import { RecordingPlayerModal, recordingKind, recordingLabel } from '../../components/MeetingRecording';
import { ActivityComposer } from './ActivityComposer';
import { ActivityDetail } from './ActivityDetail';
import { TaskDrawer } from '../agency/TaskDrawer';

/**
 * سجلّ التعامل — مكوّنٌ واحد لملف العميل المحتمل وملف العميل سواء.
 *
 * كان محفورًا في ملف العميل المحتمل وحده: تبويبات المهمة والاجتماع
 * والمكالمة والرسالة والملاحظة، وقوائمها المرقّمة الأحدثُ أولًا،
 * وحبّة تنبيه الملاحظات، والإرسال المباشر عبر القنوات، وحارس حذف
 * المنجَز. واستُخرج هنا مُعلَّمًا بالكيان — فالعميل (فردًا أو شركة)
 * يكسب القسم نفسه شكلًا وخياراتٍ وسلوكًا، ولا نسختين تفترقان.
 */

export interface HubActivity {
  id: string;
  type: 'TASK' | 'MEETING' | 'CALL' | 'MAIL' | 'NOTE';
  status: 'PLANNED' | 'DONE' | 'CANCELLED';
  subject: string;
  body: string | null;
  dueAt: string | null;
  completedAt: string | null;
  createdAt?: string;
  /** معرّف مهمة اللوح المزروعة — به يُفتح اللوح الجانبي. */
  agencyTaskId?: string | null;
  meetingUrl?: string | null;
  meetingProvider?: string | null;
  callDirection?: string | null;
  callOutcome?: string | null;
  mailTo?: string | null;
  mailSubject?: string | null;
  messageChannel?: string | null;
  noteAbout?: string | null;
  recordingUrl?: string | null;
}

export interface HubRecord {
  id: string;
  name: string;
  phone: string | null;
  altPhone?: string | null;
  email: string | null;
  /** توجيه القنوات — أيُّ الرقمين يستقبل كل قناة. الفارغ: الأساسي. */
  waPhone?: string | null;
  smsPhone?: string | null;
  mapsUrl?: string | null;
}

export function ActivityHub({
  entity,
  record,
  tabsExtra,
  chrome = 'card',
}: {
  entity: 'lead' | 'account';
  record: HubRecord;
  /** عنصرٌ يملأ فراغ سطر التبويبات — كميتر تصنيف العميل المحتمل. */
  tabsExtra?: React.ReactNode;
  /** 'card' ببطاقته ورأسه — و'bare' داخل لوحة تبويبٍ قائمة. */
  chrome?: 'card' | 'bare';
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const id = record.id;
  const link = entity === 'lead' ? { leadId: id } : { accountId: id };

  const [openTaskId, setOpenTaskId] = useState<string | null>(null);
  const [openActivityId, setOpenActivityId] = useState<string | null>(null);
  const [sendingMail, setSendingMail] = useState<HubActivity | null>(null);
  const [quickMsg, setQuickMsg] = useState<{
    channel: QuickChannel;
    activity: HubActivity;
  } | null>(null);
  /*
   * الاتصال من مكانه — لوحة الكونسول تحضر إلى السجلّ.
   *
   * كان زرّ «اتصل» رابطًا يُبحر إلى مركز الاتصال: تغادر ملف العميل
   * وتفقد ما كنت فيه لتطلب رقمًا تراه أمامك. صار كسائر أيقونات القنوات
   * في النظام: لوحةٌ راسيةٌ تبدأ المكالمة وتُنهيها بنتيجتها هنا.
   */
  const [calling, setCalling] = useState<HubActivity | null>(null);
  /* التبويب المفتوح — به يُنتقى شريطُ ملاحظاتِ ذلك الجانب. */
  const [activeTab, setActiveTab] = useState<HubActivity['type']>('CALL');
  /*
   * البحث في السجل: عميلٌ يتصل والموظف يريد نشاطًا بعينه بسرعة —
   * بالعنوان أو بالتاريخ. أثناء الكتابة تحلّ النتائج (كل الأنواع
   * معًا) محلّ التبويبات، ومسحُها يعيدها.
   */
  const [search, setSearch] = useState('');
  /* حارس حذف المنجَز — المالكُ وكلمةُ سرّ الحذف، والخادم هو الحكم. */
  const [guard, setGuard] = useState<{ id: string; label: string } | null>(null);
  const [guardPass, setGuardPass] = useState('');
  const [guardErr, setGuardErr] = useState<string | null>(null);
  const [guardBusy, setGuardBusy] = useState(false);

  const activities = useQuery({
    queryKey: ['activities', { [entity === 'lead' ? 'leadId' : 'accountId']: id }],
    queryFn: () =>
      api<{ items: HubActivity[] }>(
        `/crm/activities${qs({ ...(entity === 'lead' ? { leadId: id } : { accountId: id }), pageSize: 100 })}`,
      ),
  });
  const items = activities.data?.items ?? [];

  const refreshAll = () => {
    void queryClient.invalidateQueries({ queryKey: ['activities'] });
    void queryClient.invalidateQueries({ queryKey: ['timeline'] });
    void queryClient.invalidateQueries({ queryKey: [entity, id] });
  };

  /* لكل قناةٍ رقمُها الموجَّه — والآخر احتياطًا حين يخلو. */
  const phoneFor = (which: string | null | undefined) =>
    which === 'altPhone'
      ? (record.altPhone ?? record.phone)
      : (record.phone ?? record.altPhone ?? null);
  const waSource = phoneFor(record.waPhone);
  const smsSource = phoneFor(record.smsPhone);

  /*
   * الإرسال المباشر عبر قنوات ميتا — من الملف بلا رحلة إلى الصندوق.
   *
   * واتساب: المحادثة القائمة بالرقم تُلتقط، وإلا بُدئت على أول قناة
   * واتساب نشطة. أما ماسنجر وإنستغرام فالمنصّة تمنع بدء محادثة؛
   * يُرسَل في محادثةٍ قائمة مربوطة بهذا السجلّ، وإلا قيل ذلك بصراحة.
   */
  const sendQuick = useMutation({
    mutationFn: async ({
      channel,
      activity,
      text,
      file,
    }: {
      channel: QuickChannel;
      activity: HubActivity;
      text: string;
      file: File | null;
    }) => {
      /* SMS: تُسلَّم لتطبيق الجهاز بنصّها — ولا تُعلَّم «أُرسلت» زورًا. */
      if (channel === 'SMS') {
        if (!smsSource) throw new Error('لا رقم مسجَّل لهذا العميل.');
        window.location.href = `sms:${smsSource}?body=${encodeURIComponent(text)}`;
        return 'sms-handoff' as const;
      }

      let convId: string | null = null;

      if (channel === 'WHATSAPP') {
        const wa = whatsappNumber(waSource);
        if (!wa) throw new Error('يلزم رقم هاتف بمفتاح دولة لإرسال واتساب.');
        const found = await api<{ conversationId: string | null }>(
          `/meta/by-phone${qs({ phone: wa })}`,
        );
        convId = found.conversationId;
        if (!convId) {
          const channels = await api<Array<{ id: string; kind: string; isActive: boolean }>>(
            '/meta/channels',
          );
          const ch = channels.find((c) => c.kind === 'WHATSAPP' && c.isActive);
          if (!ch) throw new Error('لا قناة واتساب مربوطة — اربطها من الإعدادات أولًا.');
          const conv = await api<{ id: string }>('/meta/conversations', {
            method: 'POST',
            body: { channelId: ch.id, phone: wa, ...link },
          });
          convId = conv.id;
        }
      } else {
        const convs = await api<
          Array<{ id: string; leadId: string | null; accountId: string | null; channelKind: string }>
        >('/meta/conversations');
        const conv = convs.find(
          (c) =>
            c.channelKind === channel &&
            (entity === 'lead' ? c.leadId === id : c.accountId === id),
        );
        if (!conv) {
          const label = channel === 'MESSENGER' ? 'ماسنجر' : 'إنستغرام';
          throw new Error(
            `لا محادثة ${label} مربوطة بهذا العميل بعد — المنصّة لا تسمح ببدء محادثة من طرفنا. حين يراسلكم تُربط محادثته ويُرسَل من هنا مباشرةً.`,
          );
        }
        convId = conv.id;
      }

      /*
       * الوسيط عبر مساره والنص عبر مساره — وحالة «فشلت» تُرمى بسببها:
       * الردّ 200 والرسالة محفوظة فاشلةً معناه أن العميل لم يستلم شيئًا.
       */
      let view: { status: string; error: string | null };
      if (file) {
        const form = new FormData();
        form.append('file', file);
        if (text) form.append('caption', text);
        view = await upload(`/meta/conversations/${convId}/media`, form);
      } else {
        view = await api(`/meta/conversations/${convId}/messages`, {
          method: 'POST',
          body: { body: text },
        });
      }
      if (view.status === 'FAILED') {
        throw new Error(view.error ?? 'فشل الإرسال.');
      }

      await api(`/crm/activities/${activity.id}/complete`, {
        method: 'POST',
        body: { outcome: 'أُرسلت من ملف العميل عبر قنوات ميتا.' },
      });
      return 'sent' as const;
    },
    onSuccess: (kind) => {
      setQuickMsg(null);
      toast(
        kind === 'sms-handoff'
          ? 'فُتح تطبيق الرسائل بنصّك — علّم النشاط منجَزًا بعد الإرسال'
          : 'أُرسلت وسُجّلت في محادثتها — والنشاط اكتمل',
      );
      refreshAll();
      void queryClient.invalidateQueries({ queryKey: ['meta'] });
    },
  });

  const afterDelete = () => {
    toast('حُذف النشاط');
    refreshAll();
  };

  const deleteActivity = (a: HubActivity, label: string) => {
    if (a.status === 'DONE') {
      setGuard({ id: a.id, label });
      setGuardPass('');
      setGuardErr(null);
      return;
    }
    if (!window.confirm(`حذف ${label}؟ لا رجوع بعد الحذف.`)) return;
    void api(`/crm/activities/${a.id}`, { method: 'DELETE' }).then(afterDelete);
  };

  const guardedDelete = () => {
    if (!guard) return;
    setGuardBusy(true);
    setGuardErr(null);
    api(`/crm/activities/${guard.id}`, { method: 'DELETE', body: { password: guardPass } })
      .then(() => {
        setGuard(null);
        afterDelete();
      })
      .catch((e: unknown) => setGuardErr(e instanceof Error ? e.message : 'تعذّر الحذف.'))
      .finally(() => setGuardBusy(false));
  };

  /* الإنجاز من الصفّ مباشرة — للمهام والاجتماعات والمكالمات سواء. */
  const completeActivity = (actId: string) => {
    void api(`/crm/activities/${actId}/complete`, { method: 'POST', body: {} }).then(() => {
      toast('عُلّم منجَزًا');
      refreshAll();
      /* مهمة اللوح المزروعة اكتملت معه — لوحٌ مفتوحٌ لاحقًا يقرأ الجديد. */
      void queryClient.invalidateQueries({ queryKey: ['agency'] });
    });
  };

  const composer = (
    <ActivityComposer
      link={link}
      /* موقع العميل يملأ خريطة الاجتماع الحضوريّ — لا يُنسخ يدويًّا. */
      recordMapsUrl={record.mapsUrl ?? null}
      onDone={refreshAll}
      onOpenTask={setOpenTaskId}
      onTypeChange={setActiveTab}
      tabsExtra={tabsExtra}
      taskPanel={
        <HubTasks
          tasks={items.filter((a) => a.type === 'TASK')}
          onOpen={setOpenTaskId}
          onComplete={completeActivity}
          onDelete={(a) => deleteActivity(a, 'المهمة')}
        />
      }
      meetingPanel={
        <HubMeetings
          meetings={items.filter((a) => a.type === 'MEETING')}
          mapsUrl={record.mapsUrl ?? null}
          onOpen={setOpenActivityId}
          onComplete={completeActivity}
          onDelete={(a) => deleteActivity(a, 'الاجتماع')}
        />
      }
      callPanel={
        <HubCalls
          calls={items.filter((a) => a.type === 'CALL')}
          phone={record.phone}
          onOpen={setOpenActivityId}
          onComplete={completeActivity}
          onDelete={(a) => deleteActivity(a, 'المكالمة')}
          onCall={setCalling}
        />
      }
      mailPanel={
        <HubMails
          mails={items.filter((a) => a.type === 'MAIL')}
          email={record.email}
          phone={waSource}
          smsPhone={smsSource}
          onOpen={setOpenActivityId}
          onSend={setSendingMail}
          onQuick={(m) =>
            setQuickMsg({
              channel: (m.messageChannel ?? 'WHATSAPP') as QuickChannel,
              activity: m,
            })
          }
          onDelete={(a) => deleteActivity(a, 'الرسالة')}
          onComplete={completeActivity}
        />
      }
      notePanel={
        <HubNotes
          notes={items.filter((a) => a.type === 'NOTE')}
          onOpen={setOpenActivityId}
          onDelete={(a) => deleteActivity(a, 'الملاحظة')}
        />
      }
    />
  );

  const ticker = (
    <NoteTicker
      notes={items.filter(
        (a) => a.type === 'NOTE' && (activeTab === 'NOTE' || a.noteAbout === activeTab),
      )}
      tab={activeTab}
      onOpen={setOpenActivityId}
    />
  );

  /*
   * المطابقة على العنوان والنصّ والتاريخ معًا: التاريخ يُقارن بصيغته
   * المعروضة («2 سبتمبر 2026، 09:04 م») وبصيغته الرقمية (2026-09-02)
   * — فمن يكتب «سبتمبر» أو «09-02» أو جزء العنوان يجد ضالّته.
   */
  const q = search.trim();
  const searchResults = q
    ? items.filter((a) => {
        const hay = [
          a.subject,
          a.body ?? '',
          ACTIVITY_TYPE_MAP[a.type]?.label ?? '',
          dateTime(a.createdAt),
          dateTime(a.dueAt),
          (a.createdAt ?? '').slice(0, 10),
          (a.dueAt ?? '').slice(0, 10),
        ].join(' ');
        return hay.includes(q);
      })
    : [];

  /* صندوق البحث الموحّد — الشكل نفسه في القوائم والملفات والتبويبات سواء. */
  const searchBox = (
    <SearchBox value={search} onChange={setSearch} placeholder="ابحث بالعنوان أو التاريخ" />
  );

  return (
    <>
      {chrome === 'card' ? (
        <div className="card">
          <div className="card-head">
            <h2 className="card-title">سجل التعامل</h2>
            {searchBox}
            {ticker}
            <span className="small mute" style={{ flex: 'none' }}>
              {plural(items.length, WORDS.activity)}
            </span>
          </div>
          <div className="card-body">
            {q ? (
              <SearchResults
                query={q}
                results={searchResults}
                onOpenTask={setOpenTaskId}
                onOpenActivity={setOpenActivityId}
              />
            ) : (
              composer
            )}
          </div>
        </div>
      ) : (
        <>
          {/* داخل تبويبٍ قائم: الحبّة والبحث سطرًا فوق المؤلّف. */}
          <div className="row mb-3" style={{ minHeight: 4, gap: 'var(--sp-2)' }}>
            {searchBox}
            {ticker}
          </div>
          {q ? (
            <SearchResults
              query={q}
              results={searchResults}
              onOpenTask={setOpenTaskId}
              onOpenActivity={setOpenActivityId}
            />
          ) : (
            composer
          )}
        </>
      )}

      {openTaskId && (
        <TaskDrawer
          taskId={openTaskId}
          onClose={() => {
            setOpenTaskId(null);
            /* الإكمال والتسمية في اللوح يرتدّان للنشاط — فتُحدَّث القائمة. */
            refreshAll();
          }}
        />
      )}

      {openActivityId && (
        <ActivityDetail id={openActivityId} onClose={() => setOpenActivityId(null)} />
      )}

      {/*
        لوحة الاتصال راسيةً في السجلّ — كأيقونات القنوات في سائر الشاشات.
        وبإغلاقها تُحدَّث القائمة: المكالمة التي جرت للتوّ تدخل السجلّ
        بنتيجتها، فلا يُترك الصفّ يقول «مجدولة» وقد وقعت.
      */}
      {calling && record.phone && (
        <QuickCallModal
          clientName={record.name}
          phone={record.phone}
          link={link}
          onClose={() => {
            setCalling(null);
            refreshAll();
          }}
        />
      )}

      {/* حارس حذف المنجَز — المالكُ وكلمةُ سرّ الحذف، والخادم هو الحكم. */}
      {guard && (
        <Modal
          title={`حذف ${guard.label} المنجَزة`}
          onClose={() => setGuard(null)}
          footer={
            <>
              <button className="btn" onClick={() => setGuard(null)}>
                إلغاء
              </button>
              <button
                className="btn btn-danger"
                disabled={!guardPass || guardBusy}
                onClick={guardedDelete}
              >
                {guardBusy ? 'جارٍ الحذف…' : 'احذف نهائيًّا'}
              </button>
            </>
          }
        >
          {guardErr && <div className="alert alert-danger mb-3">{guardErr}</div>}
          <p className="small mute mb-3">
            هذا نشاطٌ منجَز — حذفه محوٌ لتاريخ عملٍ وقع، وهو للمالك وحده وبكلمة سرّ الحذف
            المضبوطة في «الإعدادات ← الحماية».
          </p>
          <label className="field">
            <span className="label">كلمة سرّ الحذف</span>
            <input
              className="input"
              type="password"
              value={guardPass}
              onChange={(e) => setGuardPass(e.target.value)}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter' && guardPass) guardedDelete();
              }}
            />
          </label>
        </Modal>
      )}

      {/* واتساب وماسنجر وإنستغرام وSMS — مُرسِلٌ راسٍ بلون قناته. */}
      {quickMsg && (
        <QuickMessageModal
          channel={quickMsg.channel}
          clientName={record.name}
          hint={
            quickMsg.channel === 'WHATSAPP'
              ? 'تُرسَل عبر قناة واتساب المربوطة وتُسجَّل في محادثة العميل — والوسائط كذلك.'
              : quickMsg.channel === 'MESSENGER'
                ? 'تُرسَل في محادثة العميل القائمة — والوسائط تُرفع عبر واجهة ماسنجر الرسمية.'
                : quickMsg.channel === 'SMS'
                  ? 'تُفتح في تطبيق الرسائل على جهازك بنصّها — لا بوابة SMS مربوطة بعد.'
                  : 'تُرسَل في محادثة العميل القائمة — وإنستغرام نصٌّ فقط: واجهته لا تقبل رفع وسائط.'
          }
          pending={sendQuick.isPending}
          error={sendQuick.error instanceof Error ? sendQuick.error.message : null}
          onSend={(text, file) =>
            sendQuick.mutate({
              channel: quickMsg.channel,
              activity: quickMsg.activity,
              text,
              file,
            })
          }
          onClose={() => {
            sendQuick.reset();
            setQuickMsg(null);
          }}
        />
      )}

      {/* الإرسال من الملف مباشرةً — والنشاط يُعلَّم منجزًا حين تخرج الرسالة. */}
      {sendingMail && (
        <MailCompose
          to={sendingMail.mailTo ?? record.email ?? ''}
          subject={sendingMail.mailSubject ?? sendingMail.subject}
          link={link}
          onClose={() => setSendingMail(null)}
          onSent={() => {
            void api(`/crm/activities/${sendingMail.id}/complete`, {
              method: 'POST',
              body: { outcome: 'أُرسلت من ملف العميل.' },
            }).then(refreshAll);
            setSendingMail(null);
          }}
        />
      )}
    </>
  );
}

/* ==========================================================================
   نتائج البحث — كل الأنواع معًا، الأحدث أولًا
   ========================================================================== */

const TYPE_ICON: Record<HubActivity['type'], 'clipboard' | 'video' | 'phone' | 'mail' | 'note'> = {
  TASK: 'clipboard',
  MEETING: 'video',
  CALL: 'phone',
  MAIL: 'mail',
  NOTE: 'note',
};

function SearchResults({
  query,
  results,
  onOpenTask,
  onOpenActivity,
}: {
  query: string;
  results: HubActivity[];
  onOpenTask: (agencyTaskId: string) => void;
  onOpenActivity: (id: string) => void;
}) {
  const { shown, pager } = usePagedNewestFirst(results);

  return (
    <div className="col" style={{ gap: 0 }}>
      <h3 className="label">
        نتائج البحث عن «{query}» — {plural(results.length, WORDS.activity)}
      </h3>

      {results.length === 0 && (
        <p className="tiny mute">لا نشاط يطابق — جرّب جزءًا من العنوان أو التاريخ.</p>
      )}

      {shown.map((a) => {
        const done = a.status === 'DONE';
        const open = () =>
          a.type === 'TASK' && a.agencyTaskId ? onOpenTask(a.agencyTaskId) : onOpenActivity(a.id);
        return (
          <div key={a.id} className="cc-recent-row">
            <span style={{ color: done ? 'var(--success)' : undefined, display: 'inline-flex' }}>
              <Icon name={done ? 'check' : TYPE_ICON[a.type]} size={14} />
            </span>
            <button
              type="button"
              className="cc-rowbtn cc-recent-who"
              style={done ? { opacity: 0.75 } : undefined}
              onClick={open}
              title="افتح التفاصيل"
            >
              {a.subject}
              <span className="tiny mute"> · {ACTIVITY_TYPE_MAP[a.type]?.label}</span>
            </button>
            {/* التاريخ الذي يُبحث به يُعرض بعينه — الإنشاء، والموعد إن كان. */}
            <span className="tiny mute nowrap">
              {dateTime(a.createdAt)}
              {a.status === 'PLANNED' && a.dueAt && <> — موعده {dateTime(a.dueAt)}</>}
            </span>
            <span className="row" style={{ marginInlineStart: 'auto', gap: 4 }}>
              <button className="btn btn-sm" onClick={open}>
                التفاصيل
              </button>
            </span>
          </div>
        );
      })}
      {pager}
    </div>
  );
}

/* ==========================================================================
   حبّة تنبيه الملاحظات — بفكرة شريط البريد سواء
   ========================================================================== */

const NOTE_TICKER_MS = 4000;

function NoteTicker({
  notes,
  tab,
  onOpen,
}: {
  notes: HubActivity[];
  tab: HubActivity['type'];
  onOpen: (id: string) => void;
}) {
  const [i, setI] = useState(0);
  const [listOpen, setListOpen] = useState(false);

  /* التقدّم خطوةً كلَّ تسكة؛ يتوقف حين لا تعدُّد. */
  useEffect(() => {
    if (notes.length <= 1) return;
    const t = window.setInterval(() => setI((x) => x + 1), NOTE_TICKER_MS);
    return () => window.clearInterval(t);
  }, [notes.length]);

  if (notes.length === 0) return null;

  const sorted = [...notes].sort((a, b) => ((a.createdAt ?? '') < (b.createdAt ?? '') ? 1 : -1));
  const cycling = sorted.length > 1;
  const n = sorted[i % sorted.length]!;
  const label =
    tab === 'NOTE' ? 'كل الملاحظات' : `ملاحظات تخصّ ${ACTIVITY_TYPE_MAP[tab]?.plural ?? ''}`;

  return (
    <>
      <span className="mail-ticker" aria-live="polite">
        {/* المفتاح يتبدّل كل دورة فتُعاد حركة الخفوت والعودة. */}
        <button
          type="button"
          key={`${n.id}-${i}`}
          className={`mail-ticker-chip is-in${cycling ? ' is-cycling' : ''}`}
          style={cycling ? { animationDuration: `${NOTE_TICKER_MS}ms` } : undefined}
          onClick={() => setListOpen(true)}
          title={`اعرض ${label}`}
        >
          <Icon name="note" size={13} />
          <span className="mail-ticker-text">
            <span className="mail-ticker-verb">ملاحظة</span>
            {' — '}
            {n.subject}
            <span className="mail-ticker-who"> · {dateTime(n.createdAt)}</span>
          </span>
        </button>
      </span>

      {listOpen && (
        <Modal title={label} onClose={() => setListOpen(false)}>
          <div className="col" style={{ gap: 0 }}>
            {sorted.map((x) => (
              <div key={x.id} className="cc-recent-row">
                <span style={{ display: 'inline-flex' }}>
                  <Icon name="note" size={14} />
                </span>
                <button
                  type="button"
                  className="cc-rowbtn cc-recent-who"
                  onClick={() => {
                    setListOpen(false);
                    onOpen(x.id);
                  }}
                  title="افتح تفاصيل الملاحظة"
                >
                  {x.subject}
                </button>
                <span className="tiny mute nowrap" style={{ marginInlineStart: 'auto' }}>
                  {dateTime(x.createdAt)}
                </span>
              </div>
            ))}
          </div>
        </Modal>
      )}
    </>
  );
}

/* ==========================================================================
   القوائم الخمس — الأحدث أولًا وعشرةٌ في الصفحة
   ========================================================================== */

function HubTasks({
  tasks,
  onOpen,
  onComplete,
  onDelete,
}: {
  tasks: HubActivity[];
  onOpen: (agencyTaskId: string) => void;
  onComplete: (id: string) => void;
  onDelete: (a: HubActivity) => void;
}) {
  const { shown, pager } = usePagedNewestFirst(tasks);

  if (tasks.length === 0) {
    return <p className="tiny mute">لا مهام على هذا العميل بعد — أضف أولاها.</p>;
  }

  const now = Date.now();

  return (
    <div className="col mt-3" style={{ gap: 0 }}>
      <h3 className="label">مهام هذا العميل</h3>
      {shown.map((t) => {
        const overdue = t.status === 'PLANNED' && t.dueAt && new Date(t.dueAt).getTime() < now;
        const done = t.status === 'DONE';
        return (
          <div key={t.id} className="cc-recent-row">
            <span style={{ color: done ? 'var(--success)' : undefined, display: 'inline-flex' }}>
              <Icon name={done ? 'check' : 'clipboard'} size={14} />
            </span>
            <button
              type="button"
              className="cc-rowbtn cc-recent-who"
              disabled={!t.agencyTaskId}
              style={done ? { textDecoration: 'line-through', opacity: 0.6 } : undefined}
              onClick={() => t.agencyTaskId && onOpen(t.agencyTaskId)}
              title={t.agencyTaskId ? 'افتح لوح المهمة' : 'مهمة قديمة قبل الربط بالمشاريع'}
            >
              {t.subject}
            </button>
            {t.dueAt && t.status === 'PLANNED' && (
              <span
                className="tiny mute nowrap"
                style={{ color: overdue ? 'var(--danger)' : undefined }}
              >
                {overdue ? 'فات موعدها — ' : ''}
                {dateTime(t.dueAt)}
              </span>
            )}
            <span className="row" style={{ marginInlineStart: 'auto', gap: 4 }}>
              {t.agencyTaskId && (
                <button
                  className="btn btn-sm"
                  onClick={() => onOpen(t.agencyTaskId!)}
                  title="افتح لوح المهمة"
                >
                  افتح اللوح
                </button>
              )}
              {!done && (
                <button
                  className="btn btn-sm btn-icon btn-ghost"
                  onClick={() => onComplete(t.id)}
                  title="تعليم كمنجَزة"
                  aria-label="تعليم كمنجَزة"
                >
                  <Icon name="check" size={13} />
                </button>
              )}
              <button
                className="btn btn-sm btn-icon btn-ghost btn-danger-hover"
                onClick={() => onDelete(t)}
                title="حذف المهمة"
                aria-label="حذف المهمة"
              >
                <Icon name="trash" size={13} />
              </button>
            </span>
          </div>
        );
      })}
      {pager}
    </div>
  );
}

/**
 * اجتماعات هذا العميل — القادم أولًا برابط دخوله، والمنقضي خلفه،
 * وتسجيلُ المنعقد يُشغَّل من الصفّ نفسه.
 */
function HubMeetings({
  meetings,
  mapsUrl,
  onOpen,
  onComplete,
  onDelete,
}: {
  meetings: HubActivity[];
  /** موقع العميل — يظهر مع الحضوري ليصل الموظف بلا سؤال. */
  mapsUrl: string | null;
  onOpen: (id: string) => void;
  onComplete: (id: string) => void;
  onDelete: (a: HubActivity) => void;
}) {
  const { shown, pager } = usePagedNewestFirst(meetings);
  /* تشغيل التسجيل من الصفّ — بلا رحلةٍ إلى التفاصيل لمشاهدة ما جرى. */
  const [playingRec, setPlayingRec] = useState<string | null>(null);

  if (meetings.length === 0) {
    return <p className="tiny mute">لا اجتماعات مع هذا العميل بعد — أضف أولها.</p>;
  }

  const now = Date.now();

  return (
    <div className="col" style={{ gap: 0 }}>
      <h3 className="label">اجتماعات هذا العميل</h3>
      {shown.map((m) => {
        const overdue = m.status === 'PLANNED' && m.dueAt && new Date(m.dueAt).getTime() < now;
        const done = m.status === 'DONE';
        const online =
          m.meetingProvider && m.meetingProvider !== 'ONSITE' && m.meetingProvider !== 'PHONE';
        return (
          <div key={m.id} className="cc-recent-row">
            <span style={{ color: done ? 'var(--success)' : undefined, display: 'inline-flex' }}>
              <Icon name={done ? 'check' : 'video'} size={14} />
            </span>
            <button
              type="button"
              className="cc-rowbtn cc-recent-who"
              style={done ? { textDecoration: 'line-through', opacity: 0.6 } : undefined}
              onClick={() => onOpen(m.id)}
              title="افتح تفاصيل الاجتماع"
            >
              {m.subject}
            </button>
            {m.dueAt && m.status === 'PLANNED' && (
              <span
                className="tiny mute nowrap"
                style={{ color: overdue ? 'var(--danger)' : undefined }}
              >
                {overdue ? 'فات موعده — ' : ''}
                {dateTime(m.dueAt)}
              </span>
            )}
            <span className="row" style={{ marginInlineStart: 'auto', gap: 4 }}>
              {!done && online && m.meetingUrl && (
                <a
                  className="btn btn-sm"
                  href={m.meetingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Icon name="video" size={13} />
                  ادخل
                </a>
              )}
              {/* الحضوري بموقعه — الموظف يفتح الخريطة ويذهب. */}
              {!done && !online && mapsUrl && (
                <a
                  className="btn btn-sm"
                  href={mapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="موقع العميل على الخريطة"
                >
                  <Icon name="building" size={13} />
                  الموقع
                </a>
              )}
              {/* التسجيل يُشغَّل من الصفّ — وخارجيُّ المنصّة يُفتح عندها. */}
              {m.recordingUrl &&
                (recordingKind(m.recordingUrl)?.kind === 'external' ? (
                  <a
                    className="btn btn-sm btn-icon"
                    href={m.recordingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="تسجيل الاجتماع — يُفتح على المنصّة"
                    aria-label="تسجيل الاجتماع"
                  >
                    <Icon name="video" size={13} />
                  </a>
                ) : (
                  <button
                    className="btn btn-sm btn-icon"
                    onClick={() => setPlayingRec(m.recordingUrl!)}
                    title={`شغّل ${recordingLabel(m.recordingUrl)}`}
                    aria-label={`شغّل ${recordingLabel(m.recordingUrl)}`}
                  >
                    <Icon name="video" size={13} />
                  </button>
                ))}
              {done && (
                <button className="btn btn-sm act-open is-meeting" onClick={() => onOpen(m.id)}>
                  <Icon name="video" size={13} />
                  محضر الاجتماع
                </button>
              )}
              {!done && (
                <button
                  className="btn btn-sm btn-icon btn-ghost"
                  onClick={() => onComplete(m.id)}
                  title="تعليم كمنعقد"
                  aria-label="تعليم كمنعقد"
                >
                  <Icon name="check" size={13} />
                </button>
              )}
              <button
                className="btn btn-sm btn-icon btn-ghost btn-danger-hover"
                onClick={() => onDelete(m)}
                title="حذف الاجتماع"
                aria-label="حذف الاجتماع"
              >
                <Icon name="trash" size={13} />
              </button>
            </span>
          </div>
        );
      })}
      {pager}

      {playingRec && <RecordingPlayerModal url={playingRec} onClose={() => setPlayingRec(null)} />}
    </div>
  );
}

/**
 * مكالمات هذا العميل — المجدولة أولًا بزرّ اتصالٍ يفتح الكونسول، ثم
 * المنقضية بنتيجتها. وكل صفٍّ يفتح تفاصيل المكالمة.
 */
function HubCalls({
  calls,
  phone,
  onOpen,
  onComplete,
  onDelete,
  onCall,
}: {
  calls: HubActivity[];
  phone: string | null;
  onOpen: (id: string) => void;
  onComplete: (id: string) => void;
  onDelete: (a: HubActivity) => void;
  /** يفتح لوحة الاتصال الراسية على هذه المكالمة — لا يُبحر بالكونسول. */
  onCall: (a: HubActivity) => void;
}) {
  const { shown, pager } = usePagedNewestFirst(calls);
  /*
   * تسجيلُ المكالمة يُسمع من مكانه.
   *
   * كان الصفُّ يقول «مكالمة صادرة … مهتم» ويسكت، وتسجيلُها محفوظٌ عندنا
   * لا يُبلغ عنه شيء — فمن أراد سماعَه لم يعرف أنّ ثمّ ما يُسمع. فصار
   * للصفّ زرُّ استماعٍ حين يكون له تسجيل، كصفّ الاجتماع سواء.
   */
  const [callRec, setCallRec] = useState<string | null>(null);

  if (calls.length === 0) {
    return <p className="tiny mute">لا مكالمات مع هذا العميل بعد — سجّل أولاها.</p>;
  }

  const now = Date.now();

  return (
    <div className="col" style={{ gap: 0 }}>
      <h3 className="label">مكالمات هذا العميل</h3>
      {shown.map((c) => {
        const overdue = c.status === 'PLANNED' && c.dueAt && new Date(c.dueAt).getTime() < now;
        const done = c.status === 'DONE';
        return (
          <div key={c.id} className="cc-recent-row">
            <span style={{ color: done ? 'var(--success)' : undefined, display: 'inline-flex' }}>
              <Icon name={c.callDirection === 'INBOUND' ? 'inbox' : 'phone'} size={14} />
            </span>
            <button
              type="button"
              className="cc-rowbtn cc-recent-who"
              style={done ? { opacity: 0.75 } : undefined}
              onClick={() => onOpen(c.id)}
              title="افتح تفاصيل المكالمة"
            >
              {c.subject}
              {done && c.callOutcome && <span className="tiny mute"> · {c.callOutcome}</span>}
            </button>
            {c.status === 'PLANNED' && c.dueAt && (
              <span
                className="tiny mute nowrap"
                style={{ color: overdue ? 'var(--danger)' : undefined }}
              >
                {overdue ? 'فات موعدها — ' : ''}
                {dateTime(c.dueAt)}
              </span>
            )}
            <span className="row" style={{ marginInlineStart: 'auto', gap: 4 }}>
              {!done && phone && (
                /* الاتصال في مكانه: لوحةٌ راسيةٌ تبدأ وتُسجَّل وتُنهى هنا. */
                <button className="btn btn-sm" onClick={() => onCall(c)} title={`اتصل — ${phone}`}>
                  <Icon name="phone" size={13} />
                  اتصل
                </button>
              )}
              {c.recordingUrl && (
                <button
                  className="btn btn-sm btn-icon"
                  onClick={() => setCallRec(c.recordingUrl!)}
                  title={`شغّل ${recordingLabel(c.recordingUrl)}`}
                  aria-label={`شغّل ${recordingLabel(c.recordingUrl)}`}
                >
                  <Icon name="video" size={13} />
                </button>
              )}
              {done && (
                <button className="btn btn-sm act-open is-call" onClick={() => onOpen(c.id)}>
                  <Icon name="phone" size={13} />
                  تفاصيل المكالمة
                </button>
              )}
              {!done && (
                <button
                  className="btn btn-sm btn-icon btn-ghost"
                  onClick={() => onComplete(c.id)}
                  title="تعليم كمنجَزة"
                  aria-label="تعليم كمنجَزة"
                >
                  <Icon name="check" size={13} />
                </button>
              )}
              <button
                className="btn btn-sm btn-icon btn-ghost btn-danger-hover"
                onClick={() => onDelete(c)}
                title="حذف المكالمة"
                aria-label="حذف المكالمة"
              >
                <Icon name="trash" size={13} />
              </button>
            </span>
          </div>
        );
      })}
      {pager}
      {callRec && (
        <RecordingPlayerModal url={callRec} onClose={() => setCallRec(null)} />
      )}
    </div>
  );
}

/**
 * رسائل هذا العميل — بقنواتها: البريد وواتساب وماسنجر وإنستغرام تُرسَل
 * من الملف مباشرةً وتُعلَّم منجزةً مع الإرسال، وSMS من تطبيق الجهاز
 * حتى تُربط بوابة في الإعدادات — ولها «تمّ» شهادةَ مرسِلها.
 */
function HubMails({
  mails,
  email,
  phone,
  smsPhone,
  onOpen,
  onSend,
  onQuick,
  onDelete,
  onComplete,
}: {
  mails: HubActivity[];
  email: string | null;
  /** رقم واتساب الموجَّه. */
  phone: string | null;
  /** رقم SMS الموجَّه — قد يخالف رقم واتساب. */
  smsPhone: string | null;
  onOpen: (id: string) => void;
  onSend: (mail: HubActivity) => void;
  onQuick: (mail: HubActivity) => void;
  onDelete: (a: HubActivity) => void;
  onComplete: (id: string) => void;
}) {
  const { shown, pager } = usePagedNewestFirst(mails);

  if (mails.length === 0) {
    return <p className="tiny mute">لا رسائل مع هذا العميل بعد — أضف أولاها.</p>;
  }

  const now = Date.now();
  const wa = whatsappNumber(phone);

  return (
    <div className="col" style={{ gap: 0 }}>
      <h3 className="label">رسائل هذا العميل</h3>
      {shown.map((m) => {
        const overdue = m.status === 'PLANNED' && m.dueAt && new Date(m.dueAt).getTime() < now;
        const done = m.status === 'DONE';
        const channel = (m.messageChannel ?? 'EMAIL') as MessageChannel;
        const to = m.mailTo ?? email;

        /* إجراء الإرسال بحسب القناة — كلٌّ يفتح أداته الحقيقية. */
        let action: React.ReactNode;
        if (done) {
          action = (
            <button className="btn btn-sm act-open is-mail" onClick={() => onOpen(m.id)}>
              <Icon name="mail" size={13} />
              نصّ الرسالة
            </button>
          );
        } else if (channel === 'EMAIL') {
          action = to ? (
            <button className="btn btn-sm" onClick={() => onSend(m)}>
              <Icon name="send" size={13} />
              أرسل الآن
            </button>
          ) : (
            <span className="tiny mute">بلا بريد مسجَّل</span>
          );
        } else if (channel === 'WHATSAPP') {
          /* إرسالٌ مباشر من الملف — يصل فورًا ويُسجَّل في محادثته. */
          action = wa ? (
            <button className="btn btn-sm" onClick={() => onQuick(m)}>
              <Icon name="send" size={13} />
              أرسل الآن
            </button>
          ) : (
            <span className="tiny mute">يلزم رقم بمفتاح دولة</span>
          );
        } else if (channel === 'SMS') {
          /* تُكتب في المُرسِل بإيموجيها وقوالبها ثم تُسلَّم لتطبيق الجهاز. */
          action = smsPhone ? (
            <button className="btn btn-sm" onClick={() => onQuick(m)}>
              <Icon name="sms" size={13} />
              أرسل SMS
            </button>
          ) : (
            <span className="tiny mute">لا رقم مسجَّل</span>
          );
        } else {
          /* ماسنجر وإنستغرام: يُرسَل في المحادثة القائمة — والغائبة يُقال. */
          action = (
            <button className="btn btn-sm" onClick={() => onQuick(m)}>
              <Icon name="send" size={13} />
              أرسل الآن
            </button>
          );
        }

        return (
          <div key={m.id} className="cc-recent-row">
            <span style={{ color: done ? 'var(--success)' : undefined, display: 'inline-flex' }}>
              <Icon
                name={done ? 'check' : channel === 'EMAIL' ? 'mail' : channel === 'SMS' ? 'sms' : 'chat'}
                size={14}
              />
            </span>
            <button
              type="button"
              className="cc-rowbtn cc-recent-who"
              style={done ? { opacity: 0.75 } : undefined}
              onClick={() => onOpen(m.id)}
              title="افتح تفاصيل الرسالة"
            >
              {m.subject}
              <span className="tiny mute"> · {MESSAGE_CHANNEL_MAP[channel]?.label ?? channel}</span>
            </button>
            {m.status === 'PLANNED' && m.dueAt && (
              <span
                className="tiny mute nowrap"
                style={{ color: overdue ? 'var(--danger)' : undefined }}
              >
                {overdue ? 'فات موعدها — ' : ''}
                {dateTime(m.dueAt)}
              </span>
            )}
            <span className="row" style={{ marginInlineStart: 'auto', gap: 4 }}>
              {action}
              {/*
                «تمّ» على كل قناة — صفٌّ واحد الشكل. البريد يكتمل وحده
                عند الإرسال من هنا، والزرّ لمن أرسل من خارج النظام.
              */}
              {!done && (
                <button
                  className="btn btn-sm btn-icon btn-ghost"
                  onClick={() => onComplete(m.id)}
                  title="أُرسلت — علّمها منجزة"
                  aria-label="تمّ الإرسال"
                >
                  <Icon name="check" size={13} />
                </button>
              )}
              <button
                className="btn btn-sm btn-icon btn-ghost btn-danger-hover"
                onClick={() => onDelete(m)}
                title="حذف الرسالة"
                aria-label="حذف الرسالة"
              >
                <Icon name="trash" size={13} />
              </button>
            </span>
          </div>
        );
      })}
      {pager}
    </div>
  );
}

/**
 * سجلّ ملاحظات هذا العميل — تبويب «ملاحظة» على نمط بقية التبويبات.
 *
 * الملاحظة مؤرَّخة بحكم كونها نشاطًا، فتاريخ كتابتها وساعتها يظهران
 * بجوارها لا في تلميحٍ يُبحث عنه — ومَن يقرأ يعرف متى قيل ما قيل.
 */
function HubNotes({
  notes,
  onOpen,
  onDelete,
}: {
  notes: HubActivity[];
  onOpen: (id: string) => void;
  onDelete: (a: HubActivity) => void;
}) {
  const { shown, pager } = usePagedNewestFirst(notes);

  if (notes.length === 0) {
    return <p className="tiny mute">لا ملاحظات على هذا العميل بعد — اكتب أولاها.</p>;
  }

  return (
    <div className="col" style={{ gap: 0 }}>
      <h3 className="label">ملاحظات هذا العميل</h3>
      {shown.map((n) => (
        <div key={n.id} className="cc-recent-row">
          <span style={{ display: 'inline-flex' }}>
            <Icon name="note" size={14} />
          </span>
          <button
            type="button"
            className="cc-rowbtn cc-recent-who"
            onClick={() => onOpen(n.id)}
            title="افتح تفاصيل الملاحظة"
          >
            {n.subject}
          </button>
          {/* متى كُتبت — التاريخ والساعة ظاهران بجوار كل ملاحظة. */}
          <span className="tiny mute nowrap">
            {dateTime(n.createdAt)}
            {n.noteAbout && ACTIVITY_TYPE_MAP[n.noteAbout as HubActivity['type']] && (
              <> — تخصّ {ACTIVITY_TYPE_MAP[n.noteAbout as HubActivity['type']]!.plural}</>
            )}
          </span>
          <span className="row" style={{ marginInlineStart: 'auto', gap: 4 }}>
            <button className="btn btn-sm act-open is-note" onClick={() => onOpen(n.id)}>
              <Icon name="note" size={13} />
              نصّ الملاحظة
            </button>
            <button
              className="btn btn-sm btn-icon btn-ghost btn-danger-hover"
              onClick={() => onDelete(n)}
              title="حذف الملاحظة"
              aria-label="حذف الملاحظة"
            >
              <Icon name="trash" size={13} />
            </button>
          </span>
        </div>
      ))}
      {pager}
    </div>
  );
}
